import type { StreamObject } from '../components/ai-chat-messages';
import {
  type AssistantSourceManifest,
  extractAssistantSourceManifest,
  type ReusableTurnSource,
  reusableTurnSourcesFromManifest,
} from './assistant-source-manifest';

export type AssistantTurnPayload = {
  markdown: string;
  attachments: string[];
  streamObjects: StreamObject[];
  sources?: AssistantSourceManifest | null;
  sessionId?: string;
  messageId?: string;
};

export type ChatActionNotificationAccent =
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

export type ChatActionResult =
  | boolean
  | {
      success: boolean;
      toast?: string;
      accent?: ChatActionNotificationAccent;
    };

export type NormalizedChatActionResult = {
  success: boolean;
  toast?: string;
  accent: ChatActionNotificationAccent;
};

const MAX_REUSABLE_SOURCES = 12;
const MAX_SOURCE_SNIPPET_LENGTH = 240;

export function normalizeChatActionResult(
  result: ChatActionResult,
  fallbackToast: string
): NormalizedChatActionResult {
  if (typeof result === 'boolean') {
    return {
      success: result,
      toast: result ? fallbackToast : undefined,
      accent: result ? 'success' : 'error',
    };
  }

  return {
    success: result.success,
    toast: result.toast ?? (result.success ? fallbackToast : undefined),
    accent: result.accent ?? (result.success ? 'success' : 'error'),
  };
}

export function buildReusableAssistantTurnMarkdown(
  payload: Pick<AssistantTurnPayload, 'markdown' | 'streamObjects' | 'sources'>
): string {
  const body = payload.markdown.trim();
  const sources = payload.sources
    ? reusableTurnSourcesFromManifest(payload.sources)
    : extractReusableTurnSources(payload.streamObjects);

  if (!sources.length || hasSourcesSection(body)) {
    return body;
  }

  const sourceLines = sources.slice(0, MAX_REUSABLE_SOURCES).map(source => {
    const label = source.url
      ? `[${escapeMarkdownLinkText(source.label)}](${source.url})`
      : source.label;
    const snippet = source.snippet
      ? ` - ${truncateSourceSnippet(source.snippet)}`
      : '';
    return `- ${label}${snippet}`;
  });

  return [body, `### Quellen\n${sourceLines.join('\n')}`]
    .filter(Boolean)
    .join('\n\n');
}

export function extractReusableTurnSources(
  streamObjects: StreamObject[]
): ReusableTurnSource[] {
  return reusableTurnSourcesFromManifest(
    extractAssistantSourceManifest(streamObjects)
  );
}

function hasSourcesSection(markdown: string): boolean {
  return /(?:^|\n)#{1,6}\s+(?:Quellen|Sources)\b/i.test(markdown);
}

function escapeMarkdownLinkText(value: string): string {
  return value.replace(/[[\]\\]/g, '\\$&');
}

function truncateSourceSnippet(value: string): string {
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized.length <= MAX_SOURCE_SNIPPET_LENGTH) {
    return normalized;
  }
  return `${normalized.slice(0, MAX_SOURCE_SNIPPET_LENGTH - 3).trim()}...`;
}
